import React, { useEffect, useState, useRef, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { supabase } from './lib/supabase';
import { useAuthStore } from './store/authStore';
import { useFinanceStore } from './store/useFinanceStore';
import { Auth } from './pages/Auth';
import AuthCallback from './pages/AuthCallback';
import ResetPassword from './pages/ResetPassword';
import { Dashboard } from './pages/Dashboard';
import LandingPage from './pages/LandingPage';
import { Toaster } from 'sonner';
import { LoadingProvider, useLoadingContext } from './context/LoadingContext';
import { Loader } from './components/common/Loader';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AdminRoute } from './components/AdminRoute';
import { WelcomeModal } from './components/common/WelcomeModal';
import PostAccountCreationTour from './components/PostAccountCreationTour';
import ContextualTourTrigger from './components/ContextualTourTrigger';
import { Analytics } from '@vercel/analytics/react';
import { useNotificationStore } from './store/notificationStore';
import { useNotificationsStore } from './store/notificationsStore';
import { urgentNotificationService } from './lib/urgentNotifications';
import { MobileSidebarProvider } from './context/MobileSidebarContext';
import { useThemeStore } from './store/themeStore';
import { AppInstallBanner } from './components/AppInstallBanner';
import WelcomeOnboarding from './components/WelcomeOnboarding';
import { isAndroidApp } from './utils/platformDetection';
import { isFirstLaunch } from './utils/firstLaunch';
import { App as CapacitorApp } from '@capacitor/app';
import { markPersistentLogin } from './utils/authStorage';
import { shouldRejectStoredSession, isConfirmedUser, syncUserFromSession } from './utils/authSession';
import { Capacitor } from '@capacitor/core';
import { useMobileDetection } from './hooks/useMobileDetection';

// Lazy load non-critical components for code splitting
const About = lazy(() => import('./pages/About'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogDetail = lazy(() => import('./pages/BlogDetail'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));
const AccountsView = lazy(() => import('./components/Accounts/AccountsView').then(m => ({ default: m.AccountsView })));
const TransactionsView = lazy(() => import('./components/Transactions/TransactionsView').then(m => ({ default: m.TransactionsView })));
const GlobalShoppingListPage = lazy(() =>
  import('./components/Transactions/GlobalShoppingListPage').then((m) => ({ default: m.GlobalShoppingListPage }))
);
const TransfersView = lazy(() => import('./components/Transfers/TransfersView').then(m => ({ default: m.TransfersView })));
const TransfersTableView = lazy(() => import('./components/Transfers/TransfersTableView').then(m => ({ default: m.TransfersTableView })));
const Transfer_new = lazy(() => import('./components/Transfers/Transfer_new').then(m => ({ default: m.Transfer_new })));
const SavingsView = lazy(() => import('./components/Savings/SavingsView').then(m => ({ default: m.SavingsView })));
const PurchaseTracker = lazy(() => import('./components/Purchases/PurchaseTracker').then(m => ({ default: m.PurchaseTracker })));
const LendBorrowTableView = lazy(() => import('./components/LendBorrow/LendBorrowTableView').then(m => ({ default: m.LendBorrowTableView })));
const PurchaseCategories = lazy(() => import('./components/Purchases/PurchaseCategories').then(m => ({ default: m.PurchaseCategories })));
const PurchaseAnalytics = lazy(() => import('./components/Purchases/PurchaseAnalytics').then(m => ({ default: m.PurchaseAnalytics })));
const LendBorrowAnalytics = lazy(() => import('./components/LendBorrow/LendBorrowAnalytics').then(m => ({ default: m.LendBorrowAnalytics })));
const AnalyticsView = lazy(() => import('./components/Reports/AnalyticsView').then(m => ({ default: m.AnalyticsView })));
const ClientList = lazy(() => import('./components/Clients/ClientList').then(m => ({ default: m.ClientList })));
const MailCampaigns = lazy(() => import('./components/Clients/MailCampaigns').then(m => ({ default: m.MailCampaigns })));
const Settings = lazy(() => import('./components/Dashboard/Settings').then(m => ({ default: m.Settings })));
const LastWishPage = lazy(() => import('./pages/LastWishPage').then(m => ({ default: m.LastWishPage })));
const PaymentHistoryPage = lazy(() => import('./pages/PaymentHistoryPage').then(m => ({ default: m.PaymentHistoryPage })));
const HelpAndSupport = lazy(() => import('./pages/HelpAndSupport'));
const Investments = lazy(() => import('./pages/BusinessInvestments').then(m => ({ default: m.BusinessInvestments })));
const History = lazy(() => import('./pages/History').then(m => ({ default: m.History })));
const PublicHelpCenter = lazy(() => import('./pages/PublicHelpCenter'));
const TopicClusterHub = lazy(() => import('./pages/TopicClusterHub'));
const PublicArticlePage = lazy(() => import('./pages/PublicArticlePage'));
const SitemapPage = lazy(() => import('./pages/SitemapPage'));
// robots.txt is served as static file - no component needed
const DonationsSavingsPage = lazy(() => import('./pages/DonationsSavingsPage'));
const ZakahPage = lazy(() => import('./pages/ZakahPage').then(m => ({ default: m.ZakahPage })));
const PersonalGrowth = lazy(() => import('./components/Dashboard/PersonalGrowth').then(m => ({ default: m.PersonalGrowth })));
const NotesDiaryPage = lazy(() => import('./components/Notes/NotesDiaryPage').then(m => ({ default: m.NotesDiaryPage })));
const KBArticlePage = lazy(() => import('./pages/KBArticlePage'));
const KBSitemapPage = lazy(() => import('./pages/KBSitemapPage'));
const KBRobotsPage = lazy(() => import('./pages/KBRobotsPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const FileRenameAdmin = lazy(() => import('./pages/FileRenameAdmin').then(m => ({ default: m.FileRenameAdmin })));
const TestTaskablePanel = lazy(() => import('./components/TestTaskablePanel').then(m => ({ default: m.TestTaskablePanel })));
const DashboardDemo = lazy(() => import('./pages/DashboardDemo'));
const DashboardDemoOnly = lazy(() => import('./pages/DashboardDemoOnly'));
const ShortUrlRedirect = lazy(() => import('./pages/ShortUrlRedirect'));
const NotFound = lazy(() => import('./pages/NotFound'));

// HomeRoute component for Options 1 & 2: Platform-aware routing and first launch detection
const HomeRoute: React.FC = () => {
  const user = useAuthStore((state) => state.user);
  
  // If user is logged in, go to dashboard
  if (user) {
    return <Dashboard />;
  }
  
  // Check if this is first launch AND Android app - only show WelcomeOnboarding for Android app
  if (isFirstLaunch() && isAndroidApp()) {
    return <WelcomeOnboarding />;
  }
  
  // Check if running in Android app (not first launch)
  if (isAndroidApp()) {
    return <Navigate to="/auth" replace />;
  }
  
  // Default: show landing page for web users (including first launch on web)
  return <LandingPage />;
};

function AppContent() {
  const user = useAuthStore((state) => state.user);
  const profile = useAuthStore((state) => state.profile);
  const { isDarkMode } = useThemeStore();
  const handleEmailConfirmation = useAuthStore((state) => state.handleEmailConfirmation);
  const [loading, setLoading] = useState(true);
  const { isLoading: globalLoading, loadingMessage } = useLoadingContext();
  const { isMobile } = useMobileDetection();
  
  // Welcome modal state
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [welcomeModalChecked, setWelcomeModalChecked] = useState(false);
  const welcomeModalCheckRef = useRef(false);
  
  // Post-account creation tour state
  const [showPostAccountTour, setShowPostAccountTour] = useState(false);
  const { fetchAccounts, fetchAllData } = useFinanceStore();
  const { initializeDefaultNotifications } = useNotificationStore();
  const { fetchNotifications } = useNotificationsStore();
  
  // Function to trigger post-account creation tour
  const handleStartPostAccountTour = () => {
    // Reset first, then set to true to ensure clean state
    setShowPostAccountTour(false);
    setTimeout(() => {
      setShowPostAccountTour(true);
    }, 100);
  };
  

  // Initialize theme on app load
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  // Detect Capacitor/Android and add class for status bar padding
  useEffect(() => {
    const isCapacitor = window.Capacitor !== undefined;
    const isAndroid = /Android/i.test(navigator.userAgent);
    
    if (isCapacitor && isAndroid) {
      document.body.classList.add('capacitor-android');
    }
  }, []);

  // SMART Pull-to-Refresh: Refresh at top, scroll everywhere else
  useEffect(() => {
    const isAndroid = /Android/i.test(navigator.userAgent);
    
    if (isAndroid) {
      // Setup body constraints for proper scrolling
      const setBodyHeight = () => {
        document.body.style.height = '100vh';
        document.body.style.position = 'fixed';
        document.body.style.overflow = 'hidden';
        document.body.style.width = '100%';
        
        const rootElement = document.getElementById('root');
        if (rootElement) {
          rootElement.style.height = '100vh';
          rootElement.style.overflowY = 'auto';
          rootElement.style.overflowX = 'hidden';
          rootElement.style.WebkitOverflowScrolling = 'touch';
          rootElement.style.overscrollBehavior = 'auto'; // Allow overscroll for refresh
        }
      };
      
      setBodyHeight();
      window.addEventListener('resize', setBodyHeight);
      
      // Note: Custom Pull-to-Refresh is disabled
      
      return () => {
        window.removeEventListener('resize', setBodyHeight);
        const rootElement = document.getElementById('root');
        if (rootElement) {
          rootElement.style.height = '';
          rootElement.style.overflowY = '';
          rootElement.style.overflowX = '';
          rootElement.style.WebkitOverflowScrolling = '';
          rootElement.style.overscrollBehavior = '';
        }
        document.body.style.height = '';
        document.body.style.position = '';
        document.body.style.overflow = '';
        document.body.style.width = '';
      };
    }
    
    return () => {};
  }, []);


  useEffect(() => {
    let bootstrapDone = false;
    const finishBootstrap = () => {
      if (!bootstrapDone) {
        bootstrapDone = true;
        setLoading(false);
      }
    };
    const timeoutId = setTimeout(finishBootstrap, 10000);

    const { setUserAndProfile } = useAuthStore.getState();

    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (sessionStorage.getItem('registrationInProgress') === 'true') return;

        const currentUser = session?.user;

        if (event === 'INITIAL_SESSION') {
          if (shouldRejectStoredSession()) {
            await supabase.auth.signOut();
            await setUserAndProfile(null, null);
          } else {
            await syncUserFromSession(currentUser, setUserAndProfile);
          }
          finishBootstrap();
          return;
        }

        switch (event) {
          case 'SIGNED_IN':
          case 'USER_UPDATED':
            await syncUserFromSession(currentUser, setUserAndProfile);
            break;
          case 'SIGNED_OUT': {
            const currentState = useAuthStore.getState();
            useAuthStore.setState({ ...currentState, user: null, profile: null });
            break;
          }
          case 'TOKEN_REFRESHED':
            if (isConfirmedUser(currentUser)) {
              await setUserAndProfile(currentUser!, null);
            } else if (currentUser) {
              const currentState = useAuthStore.getState();
              useAuthStore.setState({ ...currentState, user: null, profile: null });
            }
            break;
        }
      }
    );

    (async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const accessToken = urlParams.get('access_token');
        const refreshToken = urlParams.get('refresh_token');
        if (accessToken && refreshToken) {
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (!error && data.user) await handleEmailConfirmation();
        }
      } catch {
        // continue — INITIAL_SESSION completes bootstrap
      }
    })();

    const handlePageUnload = () => {
      if (!isAndroidApp() && shouldRejectStoredSession()) {
        // Clear Supabase session from localStorage directly (synchronous)
        // Supabase stores session with key pattern: sb-{project-ref}-auth-token
        try {
          // Clear all Supabase auth-related keys from localStorage
          Object.keys(localStorage).forEach(key => {
            if (key.includes('supabase') || (key.startsWith('sb-') && key.includes('auth'))) {
              localStorage.removeItem(key);
            }
          });
        } catch (error) {
          // Ignore errors during unload
        }
      }
    };
    
    // Add page unload listeners for web
    if (!isAndroidApp()) {
      window.addEventListener('beforeunload', handlePageUnload);
      window.addEventListener('pagehide', handlePageUnload);
    }
    
    return () => {
      clearTimeout(timeoutId);
      authListener.subscription.unsubscribe();
      if (!isAndroidApp()) {
        window.removeEventListener('beforeunload', handlePageUnload);
        window.removeEventListener('pagehide', handlePageUnload);
      }
    };
  }, [handleEmailConfirmation]);

  // Handle deep link OAuth callback on Android
  useEffect(() => {
    const platform = Capacitor.getPlatform();
    console.error('[DEEPLINK] ========================================');
    console.error('[DEEPLINK] ========== SETTING UP DEEP LINK LISTENER ==========');
    console.error('[DEEPLINK] Platform:', platform);
    console.error('[DEEPLINK] ========================================');
    
    if (platform === 'android') {
      console.error('[DEEPLINK] ✅ Android platform detected - setting up deep link handlers');
      
      const handleOAuthCallback = async (url: string) => {
        console.error('[DEEPLINK] ========================================');
        console.error('[DEEPLINK] ========== DEEP LINK CALLBACK RECEIVED ==========');
        console.error('[DEEPLINK] 🔗 Full callback URL:', url);
        console.error('[DEEPLINK] URL length:', url?.length);
        console.error('[DEEPLINK] Contains /auth/callback?', url?.includes('/auth/callback'));
        console.error('[DEEPLINK] ========================================');
        
        // Check if this is the OAuth callback
        if (url && url.includes('/auth/callback')) {
          try {
            const urlObj = new URL(url);
            
            // Supabase OAuth uses hash fragments (#access_token=...) not query params
            // Parse hash fragment if present
            let accessToken: string | null = null;
            let refreshToken: string | null = null;
            let error: string | null = null;
            
            // Check hash fragment first (Supabase standard)
            if (urlObj.hash) {
              const hashParams = new URLSearchParams(urlObj.hash.substring(1)); // Remove '#'
              accessToken = hashParams.get('access_token');
              refreshToken = hashParams.get('refresh_token');
              error = hashParams.get('error') || hashParams.get('error_description');
            } else {
            }
            
            // Fallback to query params (some OAuth flows use these)
            if (!accessToken && !refreshToken && !error) {
              accessToken = urlObj.searchParams.get('access_token');
              refreshToken = urlObj.searchParams.get('refresh_token');
              error = urlObj.searchParams.get('error') || urlObj.searchParams.get('error_description');
            }
            
            
            if (error) {
              console.error('[DEEPLINK] ❌ OAuth error from deep link:', error);
              // Navigate to auth page with error
              window.location.href = '/auth?error=oauth_failed';
              return;
            }
            
            if (accessToken && refreshToken) {
              // Set the session with tokens
              const { data, error: sessionError } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken
              });
              
              if (sessionError) {
                console.error('[DEEPLINK] ❌ Error setting session:', sessionError);
                window.location.href = '/auth?error=session_failed';
                return;
              }
              
              
              if (data.user) {
                markPersistentLogin();
                const { setUserAndProfile } = useAuthStore.getState();
                await setUserAndProfile(data.user, null);
                window.location.href = '/dashboard';
              } else {
                console.error('[DEEPLINK] ❌ No user data after setting session');
                window.location.href = '/auth?error=no_user';
              }
            } else {
              // Fallback: Navigate to /auth/callback route with the full URL
              // The AuthCallback component has logic to handle OAuth callbacks
              
              // Preserve the hash and search params when navigating
              let callbackUrl = '/auth/callback';
              if (urlObj.hash) {
                callbackUrl += urlObj.hash;
              }
              if (urlObj.search) {
                callbackUrl += (urlObj.hash ? '' : urlObj.search);
              }
              
              window.location.href = callbackUrl;
            }
          } catch (error) {
            console.error('[DEEPLINK] ❌ Error handling deep link:', error);
            console.error('[DEEPLINK] Error details:', error instanceof Error ? error.message : String(error));
            window.location.href = '/auth?error=callback_failed';
          }
        } else if (url && url.includes('/auth/reset-password')) {
          // Handle password reset deep link
          try {
            const urlObj = new URL(url);
            
            // Password reset URLs can have tokens in hash or query params
            let accessToken: string | null = null;
            let refreshToken: string | null = null;
            
            // Check hash fragment first
            if (urlObj.hash) {
              const hashParams = new URLSearchParams(urlObj.hash.substring(1));
              accessToken = hashParams.get('access_token');
              refreshToken = hashParams.get('refresh_token');
            }
            
            // Fallback to query params
            if (!accessToken) {
              accessToken = urlObj.searchParams.get('access_token');
              refreshToken = urlObj.searchParams.get('refresh_token');
            }
            
            
            // Navigate to reset password page, preserving tokens
            let resetPasswordUrl = '/auth/reset-password';
            if (urlObj.hash) {
              resetPasswordUrl += urlObj.hash;
            } else if (urlObj.search) {
              resetPasswordUrl += urlObj.search;
            }
            
            window.location.href = resetPasswordUrl;
          } catch (error) {
            console.error('[DEEPLINK] ❌ Error handling password reset deep link:', error);
            console.error('[DEEPLINK] Error details:', error instanceof Error ? error.message : String(error));
            window.location.href = '/auth?error=reset_failed';
          }
        } else {
        }
      };
      
      // Check for initial URL (if app was opened via deep link)
      console.error('[DEEPLINK] 🔍 Checking for launch URL...');
      CapacitorApp.getLaunchUrl().then((result) => {
        if (result?.url) {
          console.error('[DEEPLINK] 🚀 App launched with URL:', result.url);
          console.error('[DEEPLINK] 🔄 Processing launch URL...');
          handleOAuthCallback(result.url);
        } else {
          console.error('[DEEPLINK] ℹ️ No launch URL (app opened normally)');
        }
      }).catch((err) => {
        console.error('[DEEPLINK] ℹ️ getLaunchUrl() error (normal if app opened normally):', err);
      });
      
      // Listen for app URL open events (when app is already running)
      console.error('[DEEPLINK] 👂 Setting up appUrlOpen listener...');
      const listener = CapacitorApp.addListener('appUrlOpen', (event) => {
        console.error('[DEEPLINK] ========================================');
        console.error('[DEEPLINK] 📱📱📱 appUrlOpen EVENT FIRED! 📱📱📱');
        console.error('[DEEPLINK] Event URL:', event.url);
        console.error('[DEEPLINK] ========================================');
        handleOAuthCallback(event.url);
      });
      
      console.error('[DEEPLINK] ✅ Deep link listener setup complete');
      
      return () => {
        listener.then(l => l.remove()).catch(() => {});
      };
    } else {
    }
  }, []);

  // Initialize notifications when user is authenticated
  useEffect(() => {
    if (user && !loading) {
      initializeDefaultNotifications();
      const { addHelpCenterNotification } = useNotificationStore.getState();
      addHelpCenterNotification();

      void (async () => {
        await urgentNotificationService.forceCheckUrgentNotifications(user.id);
        await fetchNotifications();
      })();
    }
  }, [user, loading, initializeDefaultNotifications, fetchNotifications]);

  // Check for premium intent after authentication
  useEffect(() => {
    if (user && !loading && profile) {
      // Check for premium intent first
      const premiumIntent = localStorage.getItem('premiumIntent');
      if (premiumIntent) {
        try {
          const intent = JSON.parse(premiumIntent);
          // Check if intent is recent (within 1 hour)
          if (Date.now() - intent.timestamp < 3600000) {
            localStorage.removeItem('premiumIntent');
            // Navigate to settings with plans tab
            setTimeout(() => {
              window.location.href = '/settings?tab=plans-usage';
            }, 1000);
            return;
          } else {
            // Remove expired intent
            localStorage.removeItem('premiumIntent');
          }
        } catch (error) {

          localStorage.removeItem('premiumIntent');
        }
      }
    }
  }, [user, loading, profile]);

  // Reset check ref when user changes
  useEffect(() => {
    if (user) {
      welcomeModalCheckRef.current = false;
      setWelcomeModalChecked(false);
    }
  }, [user?.id]);

  // Check if user has accounts and show welcome modal if needed
  useEffect(() => {
    // Prevent multiple checks
    if (welcomeModalCheckRef.current) {
      return;
    }
    
    if (user && !loading && profile) {
      const checkAndShowWelcomeModal = async () => {
        try {
          // Mark as checking to prevent re-runs
          welcomeModalCheckRef.current = true;
          
          // Ensure profile exists - wait for it if needed
          let currentProfile: import('./store/authStore').AppUser | null = profile;
          if (!currentProfile) {
            // Wait up to 3 seconds for profile to be created
            for (let i = 0; i < 6; i++) {
              await new Promise(resolve => setTimeout(resolve, 500));
              currentProfile = useAuthStore.getState().profile;
              if (currentProfile) break;
            }
          }
          
          // If profile still doesn't exist, don't show modal yet
          if (!currentProfile) {
            welcomeModalCheckRef.current = false; // Reset to allow retry
            return;
          }
          
          // Check if accounts already exist in store before fetching
          const existingAccounts = useFinanceStore.getState().accounts;
          if (existingAccounts.length > 0) {
            // User already has accounts, don't show modal
            setShowWelcomeModal(false);
            setWelcomeModalChecked(true);
            return;
          }
          
          // Add a timeout to prevent hanging (only if no accounts found)
          let timeoutId: NodeJS.Timeout | null = null;
          let timeoutTriggered = false;
          
          timeoutId = setTimeout(() => {
            timeoutTriggered = true;
            // Only show modal on timeout if we still have no accounts
            const accountsAfterTimeout = useFinanceStore.getState().accounts;
            if (accountsAfterTimeout.length === 0) {
              setShowWelcomeModal(true);
              setWelcomeModalChecked(true);
            }
          }, 5000); // 5 second timeout
          
          // Fetch accounts to get the real count
          await fetchAccounts();
          
          // Get fresh accounts count after fetch
          const freshAccounts = useFinanceStore.getState().accounts;
          const hasAccounts = freshAccounts.length > 0;
          
          // Clear timeout since we got results
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          
          // Only show modal if user has NO accounts (regardless of default currency)
          if (!hasAccounts && !timeoutTriggered) {
            setShowWelcomeModal(true);
          } else {
            setShowWelcomeModal(false);
          }
          
          // Mark as checked to prevent re-running when profile loads
          setWelcomeModalChecked(true);
        } catch (error) {
          console.error('Error checking for welcome modal:', error);
          
          // On error, check if accounts exist in store before assuming new user
          const accountsOnError = useFinanceStore.getState().accounts;
          if (accountsOnError.length > 0) {
            // User has accounts, don't show modal
            setShowWelcomeModal(false);
          } else {
            // No accounts found, might be new user - show modal
            setShowWelcomeModal(true);
          }
          setWelcomeModalChecked(true);
        }
      };
      
      checkAndShowWelcomeModal();
    }
  }, [user, loading, profile, fetchAccounts]);

  // Pull-to-refresh handler
  const handleRefresh = async () => {
    if (user) {
      await fetchAllData();
    }
  };

  if (loading) {
    return <Loader isLoading={true} message="Loading Balanze..." />;
  }

  return (
    <>
      
      {/* App Install Banner - Bottom Banner (Option B) */}
      {/* Only shows on Android mobile browsers, not in app or desktop */}
      <AppInstallBanner position="bottom" />

      <Loader isLoading={globalLoading} message={loadingMessage} />
      <Toaster 
        position={isMobile ? "top-center" : "top-right"} 
        richColors 
        expand={false}
        closeButton={true}
        duration={4000}
        theme="light"
        style={{
          borderRadius: '8px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          fontSize: '14px',
          fontWeight: '500',
          marginTop: '20px'
        }}
        toastOptions={{
          style: {
            maxWidth: '400px',
            width: 'fit-content',
            margin: '0 auto',
            padding: '12px 16px',
            borderRadius: '8px'
          }
        }}
      />
      <Suspense fallback={<Loader isLoading={true} message="Loading..." />}>
        <Routes>
          <Route path="/" element={<HomeRoute />} />
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/dashboard" element={<ProtectedRoute layout="bare"><Dashboard /></ProtectedRoute>} />
          <Route path="/login" element={user ? <Navigate to="/" replace /> : <Auth />} />
          <Route path="/register" element={user ? <Navigate to="/" replace /> : <Auth />} />
          <Route path="/auth" element={user ? <Navigate to="/" replace /> : <Auth />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/reset-password" element={<ResetPassword />} />
          
          <Route path="/accounts" element={<ProtectedRoute><AccountsView /></ProtectedRoute>} />
          <Route path="/transactions" element={<ProtectedRoute><TransactionsView /></ProtectedRoute>} />
          <Route path="/shopping-list" element={<ProtectedRoute><GlobalShoppingListPage /></ProtectedRoute>} />
          <Route path="/transfers" element={<ProtectedRoute><Transfer_new /></ProtectedRoute>} />
          <Route path="/transfers-table" element={<ProtectedRoute><TransfersTableView /></ProtectedRoute>} />
          <Route path="/transfers-new" element={<ProtectedRoute><TransfersView /></ProtectedRoute>} />
          <Route path="/savings" element={<ProtectedRoute><SavingsView /></ProtectedRoute>} />
          <Route path="/purchases" element={<ProtectedRoute><PurchaseTracker /></ProtectedRoute>} />
          <Route path="/lent-borrow" element={<ProtectedRoute><LendBorrowTableView /></ProtectedRoute>} />
          <Route path="/investments" element={<ProtectedRoute><Investments /></ProtectedRoute>} />
          <Route path="/prize-bonds" element={<ProtectedRoute><Navigate to="/investments?tab=bonds" replace /></ProtectedRoute>} />
          <Route path="/purchase-categories" element={<ProtectedRoute><PurchaseCategories /></ProtectedRoute>} />
          <Route path="/purchase-analytics" element={<ProtectedRoute><PurchaseAnalytics /></ProtectedRoute>} />
          <Route path="/lent-borrow-analytics" element={<ProtectedRoute><LendBorrowAnalytics /></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute><AnalyticsView /></ProtectedRoute>} />
          <Route path="/currency-analytics" element={<ProtectedRoute layout="bare"><Navigate to="/analytics" replace /></ProtectedRoute>} />
          <Route path="/clients" element={<ProtectedRoute><ClientList /></ProtectedRoute>} />
          <Route path="/clients/campaigns" element={<ProtectedRoute><MailCampaigns /></ProtectedRoute>} />
          <Route path="/personal-growth" element={<ProtectedRoute><PersonalGrowth /></ProtectedRoute>} />
          <Route path="/notes" element={<ProtectedRoute><NotesDiaryPage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/last-wish" element={<ProtectedRoute><LastWishPage /></ProtectedRoute>} />
          <Route path="/payment-history" element={<ProtectedRoute layout="bare"><PaymentHistoryPage /></ProtectedRoute>} />
          <Route path="/help" element={<ProtectedRoute layout="help"><HelpAndSupport /></ProtectedRoute>} />
          <Route path="/kb/:slug" element={<ProtectedRoute layout="help"><KBArticlePage /></ProtectedRoute>} />
          <Route path="/kb-sitemap.xml" element={<ProtectedRoute layout="bare"><KBSitemapPage /></ProtectedRoute>} />
          <Route path="/kb-robots.txt" element={<ProtectedRoute layout="bare"><KBRobotsPage /></ProtectedRoute>} />
          <Route path="/admin" element={<AdminRoute layout="bare"><AdminPage /></AdminRoute>} />
          <Route path="/admin/file-rename" element={<AdminRoute layout="bare"><FileRenameAdmin /></AdminRoute>} />
          {import.meta.env.DEV && (
            <Route path="/test-taskable" element={<ProtectedRoute><TestTaskablePanel /></ProtectedRoute>} />
          )}
          <Route path="/history" element={<ProtectedRoute><History /></ProtectedRoute>} />
          <Route path="/donations" element={<ProtectedRoute><DonationsSavingsPage /></ProtectedRoute>} />
          <Route path="/zakah" element={<ProtectedRoute><ZakahPage /></ProtectedRoute>} />
        
        {/* Demo routes - public */}
        <Route path="/dashboard-demo" element={<DashboardDemo />} />
        <Route path="/dashboard-demo-only" element={<DashboardDemoOnly />} />
        
        {/* Short URL redirect - public */}
        <Route path="/f/:shortCode" element={<ShortUrlRedirect />} />
        
        {/* Public routes */}
        <Route path="/about" element={<About />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogDetail />} />
        <Route path="/privacypolicy" element={<PrivacyPolicy />} />
        <Route path="/termsofservice" element={<TermsOfService />} />
        <Route path="/refundpolicy" element={<RefundPolicy />} />
        
        {/* Public Help Center Routes - SEO Optimized */}
        <Route path="/help-center" element={<PublicHelpCenter />} />
        <Route path="/help-center/topics" element={<TopicClusterHub />} />
        <Route path="/help-center/:slug" element={<PublicArticlePage />} />
        
        {/* SEO Routes */}
        <Route path="/sitemap.xml" element={<SitemapPage />} />
        {/* robots.txt is served as static file from public/robots.txt - no route needed */}
        
        {/* 404 - Catch all route must be last */}
        <Route path="/404" element={<NotFound />} />
        <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      
      {/* Welcome Modal for new users without accounts */}
      <WelcomeModal 
        isOpen={showWelcomeModal} 
        onClose={() => setShowWelcomeModal(false)}
        onStartTour={handleStartPostAccountTour}
      />
      
      {/* Post-Account Creation Tour */}
      <PostAccountCreationTour
        isOpen={showPostAccountTour}
        onClose={() => setShowPostAccountTour(false)}
        onComplete={() => {
          setShowPostAccountTour(false);
        }}
      />
      
      {/* Contextual Tour Trigger */}
      <ContextualTourTrigger       />
      
      {/* AI Chat Bot is now integrated into FloatingActionButton */}
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <LoadingProvider>
        <MobileSidebarProvider>
          <Router>
            <AppContent />
          </Router>
          {/* Test Panels removed for production */}
          <Analytics />
        </MobileSidebarProvider>
      </LoadingProvider>
    </HelmetProvider>
  );
}

export default App;

